import _ from "underscore";
import Environment from "../../common/environment";
import jstz from "jstimezonedetect";
import { ProductKey } from '@atlassiansox/bxpkit-core';
import expSignupContextBuilder from "@atlassiansox/signup-context-builder";
import { hamletApi } from "../../common/hamlet/hamlet-api";
import storage from "../../utils/storage";

export default class Cofs {
  constructor(
    paramsBaseDomain,
    paramsBaseCofsPath,
    hamlet,
    jQuery,
    experiments,
    environment = null
  ) {
    const ACCOUNT_NAME_MAX = 50;
    const ACCOUNT_NAME_MIN = 3;
    // COFS /cloud api requires that the signup context has a maximum size
    // see: https://cofs.prod.public.atl-paas.net/api.html#cloud_post
    const SIGNUP_CONTEXT_MAX_LENGTH = 8192;
    new expSignupContextBuilder();
    const RESEND_DATA = 'r.e.d';

    return {
      init: function () {
        // Nothing used here yet
      },

      /**
       * Test accounts/tenants are targeted by a specific environment and account name pattern.
       * @see https://extranet.atlassian.com/jira/browse/WCF-384
       *
       * @param {String} accountName
       * @return {Boolean}
       */
      isTestTenant(accountName) {
        return (
          this.isTestTenantByAccountName(accountName) &&
          this.isTestTenantByEnvironment()
        );
      },

      /**
       * Test accounts/tenants should only be targeted in the "staging" environment.
       * @see https://extranet.atlassian.com/jira/browse/WCF-384
       *
       * @param {String} environment
       * @return {boolean}
       */
      isTestTenantByEnvironment() {
        return environment === Environment.staging();
      },

      /**
       * Test accounts/tenants are identified by a specific account name pattern.
       * @see https://extranet.atlassian.com/jira/browse/WCF-384
       *
       * @param {String} accountName
       * @return {boolean}
       */
      isTestTenantByAccountName(accountName) {
        const TEST_TENANT_PATTERN = /^atl-vertigo--(((?:jira|j|rolex|stg)+-jira-branch-[^-]+--)|(conf-(conf|stg)-proxy-\w+--[\w-]+)).*$/;
        return TEST_TENANT_PATTERN.test(accountName);
      },

      validateCNAS: function (name) {
        const accountName = name.toLowerCase();
        const basePath = hamletApi.getCnasPath();
        const options = {
          url: `${basePath}/shopping-cart/name-availability/queries`,
          method: "POST",
          headers: {
            ContentType: "application/json"
          },
          data: {
            cloudName: accountName
          }
        };

        return hamletApi
          .request(options)
          .then(response => {
            if (response.data && response.data.availability) {
              if (response.data.availability === "AVAILABLE") {
                return Promise.resolve({ available: true });
              }
            }
            return Promise.resolve({ available: false });
          })
          .catch(() => {
            return this.validateCofsAccountName(accountName);
          });
      },

      validateCofsAccountName: function (name) {
        const accountName = name.toLowerCase();
        const basePath = hamletApi.getCofsPath();
        const options = {
          url: `${basePath}/nameAvailability`,
          method: "POST",
          headers: {
            ContentType: "application/json"
          },
          data: {
            name: accountName
          }
        };

        return hamletApi
          .request(options)
          .then(response => {
            if (response.data && response.data.success) {
              return Promise.resolve({ available: true });
            }
            return Promise.resolve({ available: false });
          })
          .catch(() => {
            return Promise.resolve({ available: false });
          });
      },

      validateCoupon: function (coupon) {
        const options = {
          url: "/validateCoupon",
          method: "POST",
          data: {
            coupon: coupon
          }
        };

        const useCofsPath = true;
        let result = { success: false };

        return hamletApi
          .hamletRequest(options, useCofsPath)
          .then(response => {
            if (response.status === 200) {
              result.success = true;
              result.eligibleProducts = response.data.eligibleProducts;

              return Promise.resolve(result);
            }
            return Promise.reject(result);
          })
          .catch(() => {
            return Promise.reject(result);
          });
      },

      createCloud: function (cofsData, submitThroughBxp = false, isLoggedIn = false) {
        const options = {
          url: "/cloud",
          method: "POST",
          data: cofsData,
          authenticated: true,
          headers: {},
        };

        const useCofsPath = true;
        const defaultError = {
          createRequest: "Cloud request error"
        };

        if (submitThroughBxp) {
          options.url = `${
            isLoggedIn ? Environment.getAPIPrivateURL() : ''
            }/cloud/signup${
            isLoggedIn ? '' : 'Anon'
            }`;

          const atlCookies = {
            _ga: storage.getCookie('_ga') || '',
            seg_xid: storage.getLocalStorage('seg_xid') || '',
            __atl_path: storage.getCookie('__atl_path') || '',
            ajs_anonymous_id: storage.getCookie('ajs_anonymous_id') || '',
          };

          options.headers['atl-cookies'] = JSON.stringify(atlCookies);

          return hamletApi.bxpRequest(options).then(response => {
            if (response.data && response.data.progressUri) {
              return Promise.resolve(response.data);
            }
            return Promise.reject(defaultError);
          })
            .catch(() => {
              return;
            });
        }
        // Bxp Express does not need this header
        options.headers["X-ATL-SOURCE"] = window.location.href;

        return hamletApi
          .hamletRequest(options, useCofsPath)
          .then(response => {
            if (response.data && response.data.progressUri) {
              return Promise.resolve(response.data);
            }

            return Promise.reject(defaultError);
          })
          .catch(() => {
            return Promise.reject(defaultError);
          });
      },

      checkCloudNameMin: function (value) {
        return value.length >= ACCOUNT_NAME_MIN;
      },

      checkCloudNameMax: function (value) {
        return value.length < ACCOUNT_NAME_MAX;
      },

      isValidCloudNamePattern: function (value) {
        return (
          value.length >= ACCOUNT_NAME_MIN &&
          /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/.test(value)
        );
      },

      isCloudIdAnnual: function (cloudId, userId) {
        const options = {
          url: "/cloud",
          method: "GET",
          authenticated: true
        };
        const defaultError = {
          validationRequest: "isCloudIdAnnual request error"
        };

        options.url += `/isCloudIdAnnual/${cloudId}?user_id=${userId}`;

        return hamletApi
          .bxpRequest(options)
          .then(response => {
            if (
              response.data &&
              typeof response.data.isCloudIdAnnual === "boolean"
            ) {
              return Promise.resolve(response.data.isCloudIdAnnual);
            }
            return Promise.reject(defaultError);
          })
          .catch(() => {
            return Promise.reject(defaultError);
          });
      },

      mapValidateData: function (formData) {
        let result = {
          contactDetails: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName
          },
          organisationDetails: null,
          onDemandAccountDetails: {
            accountName: formData.accountName,
            adminUsername: "admin",
            adminPassword: formData.aodPass,
            productKeys: formData.productKeys,
            timeZone: jstz.determine().name()
          },
          termsOfServiceAccepted: true,
          creditCardDetails: null
        };

        return result;
      },

      mapCofsData: function (formData, features) {
        let productData = [];
        _.each(formData.productKeys, function (productKey) {
          let thisProduct = {
            product: productKey,
            accountName: formData.accountName
          };

          if (formData.edition) {
            thisProduct.edition = formData.edition;
          }

          if (formData.migrationSourceUuid) {
            thisProduct.migrationSourceUuid = formData.migrationSourceUuid;
          }

          productData.push(thisProduct);
        });

        let result = {
          adminUsername: "admin",
          adminPassword: formData.aodPass,
          cloudName: formData.accountName,
          email: formData.email,
          timezone: jstz.determine().name(),
          firstName: formData.firstName,
          lastName: formData.lastName,
          products: productData
        };

        // Extract templateid and optimizely cohorts for signupContext, if available
        let signupContext = {};
        // Check for templateid param to pass in to provisioning
        if (
          formData.templateId &&
          this.isValidTemplateId(formData.templateId)
        ) {
          signupContext.templateId = formData.templateId;

          if (!formData.productKeys.includes(ProductKey.CONFLUENCE)) {
            if (!result.advancedSettings) {
              result.advancedSettings = {};
            }
            if (!result.advancedSettings.additionalProvisioningData) {
              result.advancedSettings.additionalProvisioningData = {};
            }
            result.advancedSettings.additionalProvisioningData.hideUserSegmentation = true;
          }
        }

        if (features.enableAutogenSiteName) {
          signupContext.autogenSiteName = 1;
          result.cloudName = null;
        }

        if (features.enableAutogenSiteName && !features.useSocialLogin) {
          signupContext.pagePath = 'rename/site';
        }

        // Confluence account set up flow
        if (features.enableConfluenceAccountSetup) {
          signupContext.pagePath = 'account/setup';
        }

        if (features.allowResendVerificationEmail && signupContext.pagePath) {
          var resendEmailData = {};
          resendEmailData['pagePath'] = signupContext.pagePath;
          sessionStorage.setItem(RESEND_DATA, JSON.stringify(resendEmailData));
        }

        if (Object.entries(signupContext).length > 0) {
          // Enforce COFS /cloud API restriction on signupContext size
          // see: https://cofs.prod.public.atl-paas.net/api.html#cloud_post
          let signupContextString = JSON.stringify(signupContext);
          if (signupContextString.length <= SIGNUP_CONTEXT_MAX_LENGTH) {
            result.signupContext = signupContextString;
          } else {
            // Please log to let us know when signupContext field is empty because it was too long
            // Waiting on a front end logger. https://hello.atlassian.net/browse/WPI-344
            // Backlog project to add log event when logger is available: https://hello.atlassian.net/browse/NUX-123
          }
        }

        // Coupon will be added to formdata if available
        if (formData.coupon) {
          result.coupon = formData.coupon;
        }

        // Consent will be added to formdata if available
        if (formData.consent) {
          result.consent = formData.consent;
        }

        // Data Region and Initial Product URL for Opsgenie Signup will be added if available
        if (formData.dataRegion || formData.initialProductURL) {
          const { dataRegion, initialProductURL } = formData;
          if (!result.advancedSettings) {
            result.advancedSettings = {};
          }
          if (!result.advancedSettings.additionalProvisioningData) {
            result.advancedSettings.additionalProvisioningData = {};
          }
          if (dataRegion) {
            result.advancedSettings.additionalProvisioningData.dataRegion = dataRegion;
          }
          if (initialProductURL) {
            result.advancedSettings.initialProductURL = initialProductURL;
          }
        }

        // Statuspage Signups
        const isStatusPageSignup = productData.some(
          ({ product }) => product === 'statuspage',
        );

        if (isStatusPageSignup) {
          if (!result.advancedSettings) {
            result.advancedSettings = {};
          }
          if (!result.advancedSettings.additionalProvisioningData) {
            result.advancedSettings.additionalProvisioningData = {};
          }
          result.advancedSettings.initialProductURL = (
            Environment.isStaging()
              ? 'manage.statuspagestaging.com'
              : 'manage.statuspage.io'
          );
          result.advancedSettings.additionalProvisioningData.signupLocation = 'WAC';
          result.advancedSettings.additionalProvisioningData.spProduct = 'public';
        }

        // Add Captcha Token
        if (formData.responseToken && features.submitThroughBxp) {
          result.gRecaptchaResponse = formData.responseToken;
        }

        // map experiment data to experiment object for cofs create endpoint
        if (formData.experimentKey || formData.experimentAttribute) {
          result.experiment = {};

          if (formData.experimentKey) {
            result.experiment.experimentKey = formData.experimentKey;
          }
          if (formData.experimentAttribute) {
            result.experiment.experimentAttribute =
              formData.experimentAttribute;
          }
        }

        // @see https://extranet.atlassian.com/jira/browse/WCF-384
        if (this.isTestTenant(formData.accountName)) {
          result.testingPurposes = true;
        }

        return result;
      },

      isValidTemplateId: function (templateString) {
        const validTestPattern = /^([\\.:a-zA-Z0-9_-]+)$/;
        return templateString && validTestPattern.test(templateString);
      },

      /**
       * Progress Uri structure includes request id needed for confirmation page status check
       * Object data: Progress json data
       * String progressUri: e.g. /cloud/<CLOUD_ID>/status/<REQUEST_ID_TOKEN>
       */
      getRequestId: function (progressUri) {
        const pattern = new RegExp(/cloud\/[\w-]+\/status\/([\w-]+)$/);
        const matches = pattern.exec(progressUri);

        return matches ? matches[1] : "";
      }
    };
  }
}
